import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not auth'd
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch (err) {
      // 401 here means "not logged in" — expected on first load. Log anything else.
      if (err?.response?.status !== 401 && process.env.NODE_ENV !== 'production') {
        console.warn('Auth check failed:', err?.message);
      }
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = useCallback(async (email, password, cfTurnstileResponse) => {
    const { data } = await api.post('/auth/login', {
      email,
      password,
      cf_turnstile_response: cfTurnstileResponse || undefined,
    });
    if (!data.mfa_required) setUser(data);
    return data;
  }, []);

  const verifyMfa = useCallback(async (mfaToken, code) => {
    const { data } = await api.post('/auth/mfa/verify', { mfa_token: mfaToken, code });
    setUser(data);
    return data;
  }, []);

  const register = useCallback(async (name, email, password, phone) => {
    const { data } = await api.post('/auth/register', { name, email, password, phone });
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    await api.post('/auth/logout');
    setUser(false);
  }, []);

  // Memoize the context value so consumers don't re-render on every parent render.
  const value = useMemo(
    () => ({ user, loading, login, verifyMfa, register, logout, checkAuth, refresh: checkAuth }),
    [user, loading, login, verifyMfa, register, logout, checkAuth],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
